import React, {useEffect, useMemo, useRef, useState} from 'react'
import {Box, HStack, Button, Flex, useSlotRecipe} from '@chakra-ui/react3'
import PropTypes from 'prop-types'
import {useIntl} from 'react-intl'
import {sliderSlotRecipe} from './index.recipe'
import {ArrowLeftIcon, ArrowRightIcon} from '../../icons'

function Slider({children, itemsPerSlide = 1, variant, gap = 8, sliderLabel, sliderId}) {
    const {formatMessage} = useIntl()
    const recipe = useSlotRecipe({recipe: sliderSlotRecipe})
    const styles = useMemo(() => recipe({variant}), [recipe, variant])

    const containerRef = useRef(null)
    const firstSlideRef = useRef(null)

    const items = React.Children.map(children, (child) => <>{child}</>)
    const count = items.length

    const indicatorNumber = Math.ceil(count - itemsPerSlide + 1)
    const maxIndex = Math.max(0, count - itemsPerSlide)
    const [current, setCurrent] = useState(0)
    const canPrev = current > 0
    const canNext = current < maxIndex

    const getGapX = () => {
        const el = containerRef.current
        if (!el) return 0
        const cs = window.getComputedStyle(el)
        const raw = cs.columnGap || cs.gap || '0'
        const val = parseFloat(raw)
        return Number.isFinite(val) ? val : 0
    }

    const getStep = () => {
        const slideW = firstSlideRef.current?.getBoundingClientRect().width ?? 0
        const gapX = getGapX()
        return slideW + gapX
    }

    const prev = () => {
        const el = containerRef.current
        if (!el) return
        const step = getStep()
        el.scrollTo({left: el.scrollLeft - step, behavior: 'smooth'})
    }
    const next = () => {
        const el = containerRef.current
        if (!el) return
        const step = getStep()
        el.scrollTo({left: el.scrollLeft + step, behavior: 'smooth'})
    }
    const prevNextButtons = (
        <Flex gap={2}>
            <Button
                {...styles.navButton}
                aria-controls={'slider-' + sliderId}
                aria-label={formatMessage({
                    id: 'global.gallery.scroll_prev',
                    defaultMessage: 'Scroll previous'
                })}
                onClick={prev}
                disabled={!canPrev}
            >
                <ArrowLeftIcon sx={{color: 'icons.primary'}} />
            </Button>
            <Button
                {...styles.navButton}
                aria-controls={'slider-' + sliderId}
                aria-label={formatMessage({
                    id: 'global.gallery.scroll_next',
                    defaultMessage: 'Scroll next'
                })}
                onClick={next}
                disabled={!canNext}
            >
                <ArrowRightIcon sx={{color: 'icons.primary'}} />
            </Button>
        </Flex>
    )

    useEffect(() => {
        const el = containerRef.current
        if (!el) return

        const onScroll = () => {
            const slide = firstSlideRef.current
            if (!slide) return
            const step = slide.offsetWidth + gap
            if (step <= 0) return
            const i = Math.round(el.scrollLeft / step)
            setCurrent(Math.max(0, Math.min(i, Math.max(0, count - itemsPerSlide))))
        }
        el.addEventListener('scroll', onScroll, {passive: true})
        return () => el.removeEventListener('scroll', onScroll)
    }, [count, itemsPerSlide, gap])

    return (
        <Flex
            role="region"
            aria-roledescription="carousel"
            id={'slider-' + sliderId}
            aria-label={sliderLabel}
            {...styles.sliderContainer}
            style={{
                ...styles.sliderContainer?.style,
                ['--items-per-slide']: itemsPerSlide,
                ['--slide-gap']: `var(--chakra-sizes-${gap})`
            }}
        >
            {/* Controls */}
            <Flex {...styles.controlsContainer}>
                {variant === 'gallery' ? (
                    <>
                        {prevNextButtons}
                        <HStack {...styles.pagination}>
                            {Array.from({length: indicatorNumber}).map((_, i) => (
                                <Box
                                    key={i}
                                    h="1px"
                                    w={i === current ? '36px' : '16px'}
                                    bg={
                                        i === current
                                            ? 'background.actions.on-media.active'
                                            : 'background.actions.on-media.carousel-ticker'
                                    }
                                    borderRadius="full"
                                    transition="all 0.3s ease"
                                />
                            ))}
                        </HStack>
                    </>
                ) : (
                    <Flex {...styles.videoControlContainer}>{prevNextButtons}</Flex>
                )}
            </Flex>

            <Flex
                ref={containerRef}
                className="slides"
                scrollSnapType="x mandatory"
                scrollBehavior="smooth"
                gap={gap}
                {...styles.scrollContainer}
                borderRadius={{s: 'l'}}
            >
                {items.map((child, idx) => {
                    const isVisible = idx >= current && idx < current + itemsPerSlide
                    return (
                        <Box
                            key={idx}
                            id={sliderId + '-' + idx}
                            ref={idx === 0 ? firstSlideRef : null}
                            data-testid="slide"
                            scrollSnapAlign="start"
                            scrollSnapStop="always"
                            role="group"
                            aria-roledescription="slide"
                            inert={!isVisible ? '' : undefined}
                            aria-hidden={!isVisible || undefined}
                            aria-labelledby={sliderId + '-headline-' + idx}
                            {...styles.slide}
                        >
                            {child}
                        </Box>
                    )
                })}
            </Flex>
        </Flex>
    )
}

Slider.propTypes = {
    children: PropTypes.node.isRequired,
    itemsPerSlide: PropTypes.number,
    variant: PropTypes.string,
    onIndexChange: PropTypes.func,
    gap: PropTypes.number,
    sliderLabel: PropTypes.string,
    sliderId: PropTypes.string
}

export default Slider
